import { collection, onSnapshot, query, where, orderBy } from "firebase/firestore";
import React, { useState, useEffect } from "react";
import { Container, Card, Row, Col, Table, Pagination } from "react-bootstrap";
import { db } from "../firebase-config";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

export default function InicioAdmin() {
  const [sorteos, setSorteos] = useState([]);
  const [sorteoSeleccionado, setSorteoSeleccionado] = useState(null);
  const [vendidos, setVendidos] = useState(0);
  const [transaccionesAprobadas, settransaccionesAprobadas] = useState(0);
  const [transacciones, setTransacciones] = useState([]);
  const [totalDinero, setTotalDinero] = useState(0);
  const [porcentaje, setPorcentaje] = useState(0);
  const [topCompradores, setTopCompradores] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [loading, setLoading] = useState(true);
  const [ciudadesData, setCiudadesData] = useState({
    labels: [],
    datasets: [
      {
        label: "Métodos de pago",
        data: [],
        backgroundColor: [],
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  });

  const transaccionesPorPagina = 10;

  const CHART_COLORS = {
    red: "rgb(255, 99, 132)",
    orange: "rgb(255, 159, 64)",
    yellow: "rgb(255, 205, 86)",
    green: "rgb(75, 192, 192)",
    blue: "rgb(54, 162, 235)",
    purple: "rgb(153, 102, 255)",
    grey: "rgb(201, 203, 207)",
  };

  const obtenerColoresChartJS = (etiquetas) => {
    const coloresBase = Object.values(CHART_COLORS);
    return etiquetas.map((_, i) => coloresBase[i % coloresBase.length]);
  };

  useEffect(() => {
    const sorteosRef = collection(db, "sorteos");
    const unsubscribe = onSnapshot(sorteosRef, (querySnapshot) => {
      const lista = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const ordenados = lista.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() ?? a.createdAt;
        const bTime = b.createdAt?.toMillis?.() ?? b.createdAt;
        return bTime - aTime;
      });

      setSorteos(ordenados);
      setSorteoSeleccionado((prev) => prev || ordenados[0]?.id);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!sorteoSeleccionado || sorteos.length === 0) return;

    setLoading(true);

    let ticketsCargados = false;
    let transaccionesCargadas = false;

    const checkIfLoaded = () => {
      if (ticketsCargados && transaccionesCargadas) {
        setLoading(false);
      }
    };

    const sorteo = sorteos.find((s) => s.id === sorteoSeleccionado);
    const totalNumeros = sorteo?.totalNumeros || 1;

    const ticketsRef = collection(db, `sorteos/${sorteoSeleccionado}/tickets`);
    const unsubscribeTickets = onSnapshot(ticketsRef, (querySnapshot) => {
      setVendidos(querySnapshot.size);
      const porcentaje = (querySnapshot.size / totalNumeros) * 100;
      setPorcentaje(porcentaje);
      ticketsCargados = true;
      checkIfLoaded();
    });

    const transaccionesRef = collection(db, "transacciones");
    const q = query(
      transaccionesRef,
      where("idSorteo", "==", sorteoSeleccionado),
      orderBy("Fecha", "desc")
    );

    const unsubscribeTransacciones = onSnapshot(q, (querySnapshot) => {
      const lista = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setTransacciones(lista);
      const aprobadas = lista.filter((t) => t.status === "APPROVED");
      settransaccionesAprobadas(aprobadas.length);

      const total = aprobadas.reduce((acc, t) => {
        const valor = Number(t.Valor) || 0;
        const cantidad = Number(t.Cantidad) || 0;
        return acc + valor * cantidad;
      }, 0);
      setTotalDinero(total);

      const metodosDePago = {};
      aprobadas.forEach((t) => {
        const metodo = t.metodo_pago;
        metodosDePago[metodo] = (metodosDePago[metodo] || 0) + 1;
      });

      const etiquetas = Object.keys(metodosDePago);
      const cantidades = Object.values(metodosDePago);
      const colores = obtenerColoresChartJS(etiquetas);

      setCiudadesData({
        labels: etiquetas,
        datasets: [
          {
            label: "Métodos de pago",
            data: cantidades,
            backgroundColor: colores,
            borderColor: "#fff",
            borderWidth: 2,
          },
        ],
      });

      // Top compradores: agrupar por correo o nombre y sumar Cantidad
      const compradoresMap = {};
      aprobadas.forEach((t) => {
        const key = t.Correo || t.comprador || t.Comprador || `${(t.Nombres || "").trim()} ${(t.Apellidos || "").trim()}`.trim() || "Anónimo";
        const cantidad = Number(t.Cantidad) || 0;
        compradoresMap[key] = (compradoresMap[key] || 0) + cantidad;
      });

      const topArray = Object.entries(compradoresMap)
        .map(([nombre, cantidad]) => ({ nombre, cantidad }))
        .sort((a, b) => b.cantidad - a.cantidad)
        .slice(0, 3);

      setTopCompradores(topArray);

      transaccionesCargadas = true;
      checkIfLoaded();
    });

    return () => {
      unsubscribeTickets();
      unsubscribeTransacciones();
    };
  }, [sorteoSeleccionado, sorteos]);

  const totalPaginas = Math.ceil(transacciones.length / transaccionesPorPagina);
  const transaccionesPaginadas = transacciones.slice(
    (paginaActual - 1) * transaccionesPorPagina,
    paginaActual * transaccionesPorPagina
  );

  const traducirEstado = (estado) => {
    switch (estado) {
      case "APPROVED":
        return "Aprobada";
      case "REJECTED":
        return "Rechazada";
      default:
        return "Pendiente";
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <Container>
      <Row className="mb-3">
        <Col md={4}>
          <select
            className="form-select"
            value={sorteoSeleccionado || ""}
            onChange={(e) => setSorteoSeleccionado(e.target.value)}
          >
            {sorteos.map((sorteo) => (
              <option key={sorteo.id} value={sorteo.id}>
                {sorteo.title || "Sin título"}
              </option>
            ))}
          </select>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <Card className="text-center shadow-sm p-3 mb-4 bg-white rounded">
            <div className="d-flex flex-column align-items-center">
              <i className="bi bi-list-ol" style={{ fontSize: "3rem", color: "#0897df" }}></i>
              <h3 className="mt-2">{vendidos}</h3>
              <p className="text-muted">Total números vendidos</p>
            </div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center shadow-sm p-3 mb-4 bg-white rounded">
            <div className="d-flex flex-column align-items-center">
              <i className="bi bi-check-all" style={{ fontSize: "3rem", color: "#03bd00" }}></i>
              <h3 className="mt-2">{transaccionesAprobadas}</h3>
              <p className="text-muted">Total transacciones exitosas</p>
            </div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="text-center shadow-sm p-3 mb-4 bg-white rounded">
            <div className="d-flex flex-column align-items-center">
              <i className="bi bi-coin" style={{ fontSize: "3rem", color: "#d63384" }}></i>
              <h3 className="mt-2">
                {totalDinero.toLocaleString("es-CO", {
                  style: "currency",
                  currency: "COP",
                })}
              </h3>
              <p className="text-muted">Dinero recaudado</p>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xl={6}>
          <Card className="text-center shadow-sm p-3 mb-4">
            <Card.Body>
              <Card.Title>Porcentaje de venta</Card.Title>
              <Card.Subtitle className="mb-2 text-muted">Oro laminado 18k</Card.Subtitle>
              <div style={{ width: 100, height: 100, margin: "auto" }}>
                <CircularProgressbar
                  value={porcentaje}
                  text={`${porcentaje?.toFixed(1)}%`}
                  styles={buildStyles({
                    pathColor: "#007bff",
                    textColor: "#000",
                    trailColor: "#eee",
                  })}
                />
              </div>
            </Card.Body>
            <Card.Footer className="text-muted">
              <i className="bi bi-record-circle text-primary me-1"></i> Completado
            </Card.Footer>
          </Card>
        </Col>
        <Col xl={6}>
          <Card className="text-center shadow-sm p-3 mb-4">
            <Card.Body>
              <Card.Title>Métodos de pago usados</Card.Title>
              <div style={{ width: "250px", margin: "0 auto" }}>
                <Doughnut data={ciudadesData} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm p-3 mb-4">
            <h5 className="mb-3">Top 3 compradores por cantidad</h5>
            <ul className="list-group list-group-flush text-start">
              {topCompradores.length === 0 ? (
                <li className="list-group-item">No hay compradores aún</li>
              ) : (
                topCompradores.map((c, i) => (
                  <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                    <div style={{ maxWidth: '70%' }}>{c.nombre}</div>
                    <span className="badge bg-primary rounded-pill">{c.cantidad}</span>
                  </li>
                ))
              )}
            </ul>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm p-3 mb-4">
            <h5 className="mb-3">Todas las transacciones</h5>
            <Table responsive="sm" striped hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nombres</th>
                  <th>Ciudad</th>
                  <th>Dinamica</th>
                  <th>Fecha</th>
                  <th>Instagram</th>
                  <th>Cantidad</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {transaccionesPaginadas.map((transaccion, index) => (
                  <tr key={transaccion.id}>
                    <td>{(paginaActual - 1) * transaccionesPorPagina + index + 1}</td>
                    <td>{transaccion.Nombres + " " + transaccion.Apellidos}</td>
                    <td>{transaccion.Ciudad}</td>
                    <td>{transaccion.Titulo}</td>
                    <td>{transaccion.Fecha}</td>
                    <td>{transaccion.Instagram}</td>
                    <td>{transaccion.Cantidad}</td>
                    <td>
                      <span
                        className={`badge ${
                          transaccion.status === "APPROVED"
                            ? "bg-success"
                            : transaccion.status === "REJECTED"
                            ? "bg-danger"
                            : "bg-secondary"
                        }`}
                      >
                        {traducirEstado(transaccion.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <Pagination className="justify-content-center">
              <Pagination.First onClick={() => setPaginaActual(1)} disabled={paginaActual === 1} />
              <Pagination.Prev onClick={() => setPaginaActual(paginaActual - 1)} disabled={paginaActual === 1} />
              {Array.from({ length: totalPaginas }, (_, i) => (
                <Pagination.Item key={i + 1} active={i + 1 === paginaActual} onClick={() => setPaginaActual(i + 1)}>
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next onClick={() => setPaginaActual(paginaActual + 1)} disabled={paginaActual === totalPaginas} />
              <Pagination.Last onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual === totalPaginas} />
            </Pagination>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
